import { AUTH_STORAGE_KEY } from "@/lib/backend";
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { supabase } from '@/integrations/supabase/client';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  isTransitioning: boolean;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

interface DarkModeProviderProps {
  children: React.ReactNode;
}

// Function to instantly check auth status and set theme before React renders
const initializeThemeBeforeRender = () => {
  // Check if we're in the browser
  if (typeof window === 'undefined') return;

  // Get the current stored theme
  const storedTheme = localStorage.getItem('theme');
  
  // Check for existing session synchronously from localStorage
  const existingSession = localStorage.getItem(AUTH_STORAGE_KEY);
  
  if (existingSession) {
    try {
      const sessionData = JSON.parse(existingSession);
      // If user is logged in, preserve their theme preference or use system
      if (sessionData && sessionData.access_token) {
        // User is logged in - keep their preference or use system if none set
        if (!storedTheme) {
          localStorage.setItem('theme', 'system');
          document.documentElement.className = 'system';
        }
        return;
      }
    } catch (e) {
      // Invalid session data, treat as not logged in
    }
  }
  
  // User is not logged in - force light mode
  localStorage.setItem('theme', 'light');
  document.documentElement.className = 'light';
  document.documentElement.style.colorScheme = 'light';
};

// Initialize theme immediately
initializeThemeBeforeRender();

export const DarkModeProvider = ({ children }: DarkModeProviderProps) => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Track mounted state to avoid hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  // Check authentication status and set theme accordingly
  useEffect(() => {
    if (!mounted || authChecked) return;

    const checkAuthAndSetTheme = async () => {
      try {
        // Check current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // User is logged in - allow their theme preference
          // If no theme is set, default to system
          if (!theme || theme === 'light') {
            // Only change if currently forced to light mode
            const storedTheme = localStorage.getItem('theme');
            if (storedTheme !== 'light' || !storedTheme) {
              setTheme('system');
            }
          }
        } else {
          // User is not logged in - force light mode
          if (theme !== 'light') {
            setTheme('light');
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // On error, default to light mode
        setTheme('light');
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuthAndSetTheme();
  }, [mounted, theme, setTheme, authChecked]);

  // Listen for auth state changes
  useEffect(() => {
    if (!mounted) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // User just signed in - switch to system theme or their preference
        const storedTheme = localStorage.getItem('theme');
        if (storedTheme && storedTheme !== 'light') {
          setTheme(storedTheme);
        } else {
          setTheme('system');
        }
      } else if (event === 'SIGNED_OUT') {
        // User signed out - switch to light mode
        setTheme('light');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [mounted, setTheme]);

  const isDarkMode = mounted ? resolvedTheme === 'dark' : false;

  const toggleDarkMode = () => {
    setIsTransitioning(true);
    
    // Toggle theme via next-themes (it handles localStorage automatically)
    const newTheme = isDarkMode ? 'light' : 'dark';
    setTheme(newTheme);
    
    // End transition after a short delay
    setTimeout(() => {
      setIsTransitioning(false);
    }, 150);
  };

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, isTransitioning }}>
      {children}
    </DarkModeContext.Provider>
  );
};